import { useMemo, useState } from "react";
import { Alert, Button, message, Modal, Typography } from "antd";
import { AimOutlined, EnvironmentOutlined, LoginOutlined, LogoutOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "react-query";
import moment from "moment-jalaali";
import { AttendanceApi } from "../../services";
import ModalTitle from "../ModalTitle";
import OpenStreetMap from "../OpenStreetMap";
import { toFa } from "../../utils/helpers";
import styles from "./styles.module.css";

const locate = () => new Promise((resolve, reject) => {
  if (!navigator.geolocation) return reject(new Error("geolocation is not supported"));
  navigator.geolocation.getCurrentPosition(
    ({ coords }) => resolve({ latitude: coords.latitude, longitude: coords.longitude, accuracy: coords.accuracy }),
    reject,
    { enableHighAccuracy: true, timeout: 12000, maximumAge: 15000 },
  );
});

const distance = (from, to) => {
  const earthRadius = 6371000;
  const radians = (value) => value * Math.PI / 180;
  const lat1 = radians(from.latitude);
  const lat2 = radians(to.latitude);
  const deltaLat = radians(to.latitude - from.latitude);
  const deltaLng = radians(to.longitude - from.longitude);
  const value = Math.sin(deltaLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2;
  return earthRadius * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));
};

export default function AttendancePunch({ variant = "card" }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [location, setLocation] = useState(null);
  const [locating, setLocating] = useState(false);
  const [isCheckout, setIsCheckout] = useState(false);
  const query = useQuery({
    queryKey: ["[Overview] My Attendance"],
    queryFn: () => AttendanceApi.overview({ params: { month: moment().format("YYYY-MM") } }).then((response) => response.data.overview),
    refetchInterval: 60000,
  });
  const mutation = useMutation(
    ({ out, body }) => out ? AttendanceApi.checkOut({ body }) : AttendanceApi.checkIn({ body }),
    {
      onSuccess: (_, variables) => {
        message.success(variables.out ? "خروج ثبت شد" : "ورود ثبت شد");
        setOpen(false);
        queryClient.invalidateQueries("[Overview] My Attendance");
        queryClient.invalidateQueries("[Overview] My Attendance Page");
        queryClient.invalidateQueries("[List] My Attendance Records");
        queryClient.invalidateQueries("[Today] Attendance");
      },
      onError: (error) => message.error(error?.response?.data?.message || "ثبت تردد انجام نشد"),
    },
  );

  const workplaces = useMemo(() => query.data?.workplaces || [], [query.data?.workplaces]);
  const requiredPlaces = useMemo(() => workplaces.filter((place) => place.require_location), [workplaces]);
  const nearest = useMemo(() => {
    if (!location || !workplaces.length) return null;
    const candidates = requiredPlaces.length ? requiredPlaces : workplaces;
    return candidates.map((place) => ({ ...place, distance: distance(location, place) })).sort((a, b) => a.distance - b.distance)[0];
  }, [location, requiredPlaces, workplaces]);
  const insideRequiredArea = location && requiredPlaces.some((place) => distance(location, place) <= Number(place.radius_meters));
  const canConfirm = !query.data?.location_required || Boolean(insideRequiredArea);
  const mapCenter = location || workplaces.find((place) => place.is_default) || workplaces[0];

  const refreshLocation = async () => {
    setLocating(true);
    try {
      setLocation(await locate());
    } catch {
      setLocation(null);
      message.error("موقعیت مکانی دریافت نشد؛ دسترسی Location مرورگر را فعال کنید.");
    } finally {
      setLocating(false);
    }
  };

  const showConfirmation = (out) => {
    setIsCheckout(out);
    setLocation(null);
    setOpen(true);
    refreshLocation();
  };

  const submit = () => mutation.mutate({
    out: isCheckout,
    body: location ? { latitude: location.latitude, longitude: location.longitude } : {},
  });

  const punchedIn = Boolean(query.data?.current);
  const headline = punchedIn ? "در حال حضور در محل کار" : "ثبت حضور امروز";
  const subline = punchedIn
    ? `ورود ساعت ${moment(query.data.current.check_in_at).format("HH:mm")}`
    : query.data?.location_required
      ? "موقعیت مکانی هنگام ورود کنترل می‌شود."
      : "شروع و پایان حضور خودت را ثبت کن.";
  const punchButton = (
    <Button
      className={styles.button}
      size={variant === "cell" ? "small" : "middle"}
      type="primary"
      danger={punchedIn}
      icon={punchedIn ? <LogoutOutlined /> : <LoginOutlined />}
      onClick={() => showConfirmation(punchedIn)}
    >
      {punchedIn ? "ثبت خروج" : "ثبت ورود"}
    </Button>
  );

  /* On the dashboard this rides inside TodayStrip, so it has to be built like a
     strip cell — icon, label, value, action stacked — rather than the wide
     button-on-the-right bar it uses when it stands alone. */
  return <>
    {variant === "cell" ? (
      <div className={styles.cell}>
        <span className={styles.icon}><EnvironmentOutlined /></span>
        <div className={styles.cellBody}>
          <span className={styles.cellLabel}>حضور امروز</span>
          <span className={styles.cellValue}>
            {punchedIn
              ? `ورود ساعت ${moment(query.data.current.check_in_at).format("HH:mm")}`
              : "هنوز ورودی ثبت نشده"}
          </span>
          {punchButton}
        </div>
      </div>
    ) : (
      <div className={styles.wrap}>
        <div className={styles.copy}><span className={styles.icon}><EnvironmentOutlined /></span><div><Typography.Text strong>{headline}</Typography.Text><small>{subline}</small></div></div>
        {punchButton}
      </div>
    )}

    <Modal width={760} open={open} title={<ModalTitle icon={isCheckout ? <LogoutOutlined /> : <LoginOutlined />} tone={isCheckout ? "orange" : "green"} title={isCheckout ? "تأیید موقعیت و ثبت خروج" : "تأیید موقعیت و ثبت ورود"} subtitle="موقعیت فعلی و محدوده مجاز را پیش از ثبت بررسی کنید" />} className="ds-modal" centered okText={isCheckout ? "تأیید و ثبت خروج" : "تأیید و ثبت ورود"} cancelText="انصراف" okButtonProps={{ loading: mutation.isLoading, disabled: locating || !canConfirm }} onOk={submit} onCancel={() => !mutation.isLoading && setOpen(false)} closable={!mutation.isLoading} maskClosable={false} destroyOnClose>
      <div className={styles.modalCopy}><div><strong>موقعیت فعلی شما</strong><span>محدوده‌های آبی، شعاع مجاز محل‌های کار هستند.</span></div><Button icon={<AimOutlined />} loading={locating} onClick={refreshLocation}>دریافت دوباره موقعیت</Button></div>
      <OpenStreetMap center={mapCenter} userLocation={location} workplaces={workplaces} interactive={false} height={330} />
      <div className={styles.locationResult}>
        {locating ? <Alert showIcon type="info" message="در حال دریافت موقعیت دقیق شما…" /> : !location ? <Alert showIcon type={query.data?.location_required ? "error" : "warning"} message="موقعیت شما دریافت نشده است" description={query.data?.location_required ? "بدون موقعیت مکانی امکان ثبت تردد وجود ندارد." : "می‌توانید دوباره تلاش کنید یا در صورت اختیاری بودن موقعیت، ادامه بدهید."} /> : query.data?.location_required && !insideRequiredArea ? <Alert showIcon type="error" message="خارج از محدوده مجاز هستید" description={nearest ? `فاصله شما تا «${nearest.title}» حدود ${toFa(Math.round(nearest.distance))} متر است.` : "هیچ محل کار دارای مختصات ثبت نشده است."} /> : <Alert showIcon type="success" message="موقعیت تأیید شد" description={nearest ? `نزدیک‌ترین محل: «${nearest.title}»؛ فاصله حدود ${toFa(Math.round(nearest.distance))} متر${location.accuracy ? `، دقت GPS حدود ${toFa(Math.round(location.accuracy))} متر` : ""}.` : "موقعیت فعلی همراه تردد ذخیره می‌شود."} />}
      </div>
    </Modal>
  </>;
}
